import { useMemo } from 'react';
import { z } from 'zod';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { type FellowSeedQA } from '@/constants/fellowsCorner';
import { useFeatureFlagValue } from '@/hooks/useFeatureFlag';

const questionsSchema = z.array(
  z.object({
    mentorId: z.string().min(1),
    question: z.string().min(1),
    answer: z.string().min(1),
  })
);

/**
 * Two accepted shapes for the one value. A bare array is questions alone, which
 * is what is published today; an object carries them beside the guidelines copy.
 *
 * Both are read so that adding guidelines to an environment does not take its
 * questions down in the window before the array form is republished as an
 * object, and so an environment that never gains guidelines keeps working.
 */
const contentSchema = z.union([
  questionsSchema,
  z.object({ questions: questionsSchema }).transform(({ questions }) => questions),
]);

/**
 * Questions and answers for a mentor, authored entirely as the Fellows Corner
 * flag's value in Flagsmith — there is no in-repo seed set to fall back to.
 * The same value also carries the guidelines copy, so the shape it may take is
 * either a bare array of questions or an object holding both.
 *
 * The value is authored by hand in Flagsmith, so it is parsed rather than
 * trusted: anything malformed, empty, or not shaped like the schema degrades
 * to an empty list rather than throwing inside the profile sidebar. The flag's
 * default is the boolean that gates the feature, which fails this parse and
 * lands on the same empty result. An empty list means the caller renders no
 * Questions tab at all, so an environment with no value set (or one still
 * waiting on mentor-approved content) shows the profile without one, not
 * placeholder copy.
 */
export function useFellowSeedQA(mentorId: string): FellowSeedQA[] {
  const flagValue = useFeatureFlagValue(FEATURE_FLAGS.FELLOWS_CORNER);

  return useMemo(() => {
    const parsed = contentSchema.safeParse(flagValue);

    const source = parsed.success ? parsed.data : [];

    return source.filter((item) => item.mentorId === mentorId);
  }, [flagValue, mentorId]);
}
